import axios from 'axios';
// import jwt_decode from 'jwt-decode';
// import { PublicStore, SET_JWT_TOKEN, SET_JWT_TOKEN_EXP } from '../bootstrap/ReduxStore';

import {
  APIHOST,
  APIPORT,
  APIPROTOCOL,
} from '../config';
import { attemptTokenRefresh, isTokenExpiredOrExpiringSoon } from './tokenRefresh';

const resTimeout: string =
  'Looks like the server is taking too long to respond, this can be caused by either poor connectivity or an error with our server. Please try again in a while';

const refreshToken = async (requestOptions: any) => {
  // let { JWT_TOKEN } = PublicStore.getState()
  // if (JWT_TOKEN === null && requestOptions.headers['Login-Token']) {
  //   PublicStore.dispatch({type: SET_JWT_TOKEN, data: requestOptions.headers['Login-Token']})
  //   JWT_TOKEN = requestOptions.headers['Login-Token'];
  // }
 
  // if (JWT_TOKEN !== null) {
  //   const decode: any = jwt_decode(JWT_TOKEN!)
  //   if (decode.exp * 1000 <= Date.now()) {
  //     let token = await authService.getTokenSilent();
  //     if(token){
  //       return token.idToken;
  //     }else{
  //       return undefined;
  //     }
  //   }
  // }
  // return JWT_TOKEN;

  // No prior login in this browser at all (e.g. the isUserInternal check
  // fired from the login page before the user has authenticated) — there's
  // no MSAL account to refresh against, so attemptTokenRefresh() can only
  // fail. Skip it entirely instead of retrying/timing out and firing a
  // false "Session Expired" modal before the user has even logged in.
  if (!localStorage.getItem('userType')) {
    return undefined
  }

  try {
    if (!isTokenExpiredOrExpiringSoon()) {
      const raw = localStorage.getItem('userinfo');
      if (raw) {
        const info = JSON.parse(raw);
        if (info.token) return info.token;
      }
    }
    return await attemptTokenRefresh()
  } catch {
    return undefined
  }
}

const getRequestOptions = (method: string, data: any, headers: any, reqOptions: any) => {
  const parseHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  }

  try {
    let requestOptions: any = {
      crossDomain: true,
      method,
      headers: parseHeaders,
      data: data ? data : '',
      timeout: 60000,
      validateStatus: false,
    };

    if (reqOptions) {
      requestOptions = { ...requestOptions, ...reqOptions };
    }

    if (method === 'GET') {
      delete requestOptions.data;
      if (data) {
        requestOptions.params = { ...data };
      }
    }
    return requestOptions;
  } catch (e) {
    const errorMessage: string = `Something went wrong when preparing request options to fetch data: ${e}`;
    return errorMessage;
  }
}

const doFetch = async (requestOptions: any, url: string, isRetry = false) => {
  // `skipTokenRefresh` is ours, not axios' — strip it before the request.
  // Two kinds of callers need it:
  //   - calls that already carry a known-good token in their own headers
  //     (AuthService.getUserInfo, right after a login redirect), which must
  //     not have that token silently overwritten by the lines below;
  //   - unauthenticated pre-login calls (isUserInternal/getExternalInfo).
  // Both would otherwise run attemptTokenRefresh() against the *stale*
  // localStorage['userinfo'] left behind by onSessionExpired(), and a failed
  // refresh fires 'token-expired' — showing a false "Session Expired" modal
  // during, or immediately after, a perfectly successful login.
  const skipTokenRefresh = requestOptions?.skipTokenRefresh === true;
  if (typeof requestOptions !== 'string') {
    delete requestOptions.skipTokenRefresh;
  }

  if (!skipTokenRefresh) {
    const token = await refreshToken(requestOptions);
    requestOptions.headers["Login-Token"] = token || '';
    requestOptions.headers["Token-Auth"] = token || '';
  }

  try {
    if (typeof requestOptions !== 'string') {
      const apiport = (APIPORT === '80') ? '' : `:${APIPORT}`;
      const path = url.includes('https://') || url.includes('http://') ? url : `${APIPROTOCOL}://${APIHOST}${apiport}/${url}`;
      // const path = url.includes('https://') || url.includes('http://') ? url : `${APIPROTOCOL}://${APIHOST}:${APIPORT}/${url}`;
      return axios(path, requestOptions)
        .then(async (result: any) => {
          const isUnauthorized = result?.status === 401 || result?.data?.statusCode === 401;
          if (isUnauthorized && skipTokenRefresh) {
            // Caller opted out of this tab's refresh machinery, so a 401 here
            // is theirs to interpret (AuthService.handleResponsePCA/B2C treat
            // a failed userinfo call as a failed login). Refreshing/retrying
            // behind their back would only re-introduce the false
            // "Session Expired" modal this flag exists to prevent.
            return result;
          }
          if (isUnauthorized && !isRetry) {
            // The proactive exp check above thought the token was still
            // fine, but the backend disagrees (clock skew, buffer too
            // tight, etc). Try a real refresh (retries + cross-tab lock +
            // ssoSilent) and transparently re-issue this same request once
            // with the fresh token before giving up — the caller never sees
            // this first 401 at all if it recovers.
            try {
              await attemptTokenRefresh();
              return await doFetch(requestOptions, url, true);
            } catch {
              // attemptTokenRefresh() already fired 'token-expired' internally.
              return result;
            }
          }
          if (isUnauthorized && isRetry) {
            // Already retried once with a freshly refreshed token and still
            // 401 — genuinely not a token problem, don't loop forever.
            window.dispatchEvent(new CustomEvent('token-expired'));
          }
          return result;
        })
        .catch((err) => {
          const error = JSON.parse(JSON.stringify(err));
          if (error.code === 'ECONNABORTED') {
            return { data: { message: resTimeout } };
          }
          console.log(error)
          return error;
        });
    } else {
      const errorMessage: string = requestOptions;
      return errorMessage;
    }
  } catch (e) {
    const errorMessage: string = `Something went wrong when doFetch: ${e}`;
    return errorMessage;
  }
}

export const HttpService: any = {
  get(url: string, data: any, headers: object, reqOptions: any = null) {
    try {
      const requestOptions: any = getRequestOptions('GET', data, headers, reqOptions);
      return doFetch(requestOptions, url);
    } catch (e) {
      console.log('error at get request method with error: ', e);
      return e;
    }
  },
  post(url: string, data: any, headers: object, reqOptions: any = null) {
    try {
      const requestOptions: any = getRequestOptions('POST', data, headers, reqOptions);
      return doFetch(requestOptions, url);
    } catch (e) {
      console.log('error at post request method with error: ', e);
      return e;
    }
  },
  put(url: string, data: any, headers: object, reqOptions: any = null) {
    try {
      const requestOptions: any = getRequestOptions('PUT', data, headers, reqOptions);
      return doFetch(requestOptions, url);
    } catch (e) {
      console.log('error at put request method with error: ', e);
      return e;
    }
  },
  delete(url: string, data: any, headers: object, reqOptions: any = null) {
    try {
      const requestOptions: any = getRequestOptions('DELETE', data, headers, reqOptions);
      return doFetch(requestOptions, url);
    } catch (e) {
      console.log('error at delete request method with error: ', e);
      return e;
    }
  },
};
